<template>
  <v-container class="pa-2 bg-grey-lighten-4 rounded-lg">
    <v-row>
      <v-col cols="12">
        <div class="text-body-1 text-primary">Additional Diagnosis Codes</div>
      </v-col>
      <v-col v-for="(code, ix) in diagnosisCodes" :key="ix" cols="12">
        <RemoteAutocomplete
          v-tooltip:top="getTooltipDisplayValue(code)"
          :remote-search="onChangeIcd10Code"
          :model-value="code ?? ''"
          :rules="rules"
          :label="props.label"
          :disabled="disabled"
          @update:model-value="updateCode(ix, $event)" />
        <v-btn-lh v-if="!disabled" variant="outlined" class="mt-2" color="error" @click="removeCode(ix)">
          Remove {{ code }}
        </v-btn-lh>
      </v-col>
    </v-row>
    <v-row v-if="!disabled">
      <v-col>
        <v-btn-lh variant="outlined" color="primary" :disabled="diagnosisCodes.length >= 12" @click="addCode">
          Add Another Code
        </v-btn-lh>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { computed } from "vue";
import { useFieldTooltip } from "@/composables/useFieldTooltip";
import { requiredRule, valueInItemsAutocompleteRule } from "@/utils/form";
import executor from "@/services/api";
import RemoteAutocomplete from "@/components/RemoteAutocomplete.vue";

const { getTooltipDisplayValue } = useFieldTooltip();

const props = defineProps({
  modelValue: {
    type: Array,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(["update:modelValue"]);

const diagnosisCodes = computed(() => props.modelValue);
const rules = [requiredRule, (v, remoteObj) => valueInItemsAutocompleteRule(v, "Invalid ICD 10 Code", remoteObj)];

const updateCode = (ix, value) => {
  const codes = [...props.modelValue];
  codes[ix] = value;
  emit("update:modelValue", codes);
};

const removeCode = (ix) => {
  const codes = [...props.modelValue];
  codes.splice(ix, 1);
  emit("update:modelValue", codes);
};

const addCode = () => {
  const codes = [...props.modelValue];
  codes.push("");
  emit("update:modelValue", codes);
};

const onChangeIcd10Code = (value) => {
  if (value.length > 8) return;

  const icd10SearchPromise = executor.get(`/requirements/search-icd10/?q=${value}`);
  // Axios 0.28 has an issue with AbortController, when upgrading to 1.X this can be updated
  return icd10SearchPromise.then((response) =>
    response.results.map((c) => ({ title: `${c.code} - ${c.name}`, value: c.code }))
  );
};
</script>
